import { useRef } from 'react';

import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';

import { GetDeliveryQuery } from 'data/__generated__';

import { colors } from 'constants/colors';
import { PageHeader } from './PageHeader';

/**
 * Le bon de livraison imprimable des caisses.
 *
 * Rend le document caché et expose son déclencheur, pour que l'impression vive dans le menu
 * d'actions de la page plutôt qu'en bouton perdu au milieu des informations.
 */
export const useOrderDeliveryNote = (delivery: NonNullable<GetDeliveryQuery['delivery']>) => {
    const componentRef = useRef<HTMLDivElement>(null);
    const print = useReactToPrint({ content: () => componentRef.current });
    const { boxes } = delivery;

    const printableNode = (
        <Container>
            <HiddenComponent>
                <Printable ref={componentRef}>
                    <PageHeader delivery={delivery} />
                    {boxes.map((box) => {
                        const { _id, code, number, products } = box;

                        return (
                            <BoxContainer key={_id}>
                                <Title>
                                    Caisse n°{number} - Code {code || '_____'}
                                </Title>
                                <Products>
                                    <thead>
                                        <tr>
                                            <HeaderCell>Produit</HeaderCell>
                                            <HeaderCell $center>Quantité commandée</HeaderCell>
                                            <HeaderCell $center>Quantité reçue</HeaderCell>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map((product) => {
                                            const {
                                                _id,
                                                product: { externalProductId },
                                                productLabel,
                                                quantity,
                                                quantityPicked,
                                            } = product;

                                            return (
                                                <BodyRow key={_id}>
                                                    <BodyCell>
                                                        <ProductDetails>
                                                            <ProductName>{productLabel}</ProductName>
                                                            <ProductLocation>
                                                                Référence : {externalProductId}
                                                            </ProductLocation>
                                                        </ProductDetails>
                                                    </BodyCell>
                                                    <BodyCell $center>
                                                        <QuantityValue>{quantity}</QuantityValue>
                                                    </BodyCell>
                                                    <BodyCell
                                                        $center
                                                        $hasError={
                                                            quantityPicked !== null && quantityPicked !== quantity
                                                        }
                                                    >
                                                        <QuantityValue>{quantityPicked ?? ''}</QuantityValue>
                                                    </BodyCell>
                                                </BodyRow>
                                            );
                                        })}
                                    </tbody>
                                </Products>
                            </BoxContainer>
                        );
                    })}
                </Printable>
            </HiddenComponent>
        </Container>
    );

    return { print, printableNode };
};

const Container = styled.div`
    color: ${colors.black};
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    display: table;

    @page {
        margin: 10mm;
    }
`;

const BoxContainer = styled.div`
    width: 100%;
    page-break-after: always;
`;

const Title = styled.span`
    font-size: 22px;
    font-weight: 800;
    color: ${colors.black};
`;

const Products = styled.table`
    width: 100%;
    border-spacing: 0;
    margin-top: 10px;
`;

const HeaderCell = styled.th<{ $center?: boolean }>`
    padding: 5px;
    font-size: 22px;
    text-align: ${({ $center }) => ($center ? 'center' : 'left')};
    background-color: ${colors.yellow};
`;

const BodyRow = styled.tr`
    page-break-inside: avoid;
`;

type BodyCellProps = {
    $center?: boolean;
    $hasError?: boolean;
};

const BodyCell = styled.td<BodyCellProps>`
    border-top: 1px solid ${colors.pantinGrey};
    text-align: ${({ $center }) => ($center ? 'center' : 'left')};
    color: ${({ $hasError }) => ($hasError ? colors.pantinGrey : colors.black)};
`;

const ProductDetails = styled.div`
    color: ${colors.black};
`;

const ProductName = styled.div`
    font-size: 20px;
`;

const ProductLocation = styled.div``;

const QuantityValue = styled.span`
    font-size: 22px;
`;
